import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { PhotoGallery } from '@/components/vehicles/PhotoGallery';
import { VehicleCard } from '@/components/vehicles/VehicleCard';
import { useSettings } from '@/contexts/SettingsContext';
import { getVehicle, getSimilarVehicles, trackEvent } from '@/lib/api';
import { formatPrice, formatKm, buildWhatsAppUrl, getEcoLabelColor, getStatusBadge } from '@/lib/utils';
import { toast } from 'sonner';
import { SEO, vehicleSchema } from '@/components/SEO';
import { MessageCircle, Phone, Copy, Calendar, Gauge, Fuel, Zap, MapPin, Shield, CreditCard, ArrowUpRight, Car } from 'lucide-react';

export default function VehicleDetailPage() {
    const { id } = useParams();
    const { settings } = useSettings();
    const [vehicle, setVehicle] = useState(null);
    const [similar, setSimilar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        setError(null);
        getVehicle(id)
            .then(res => {
                setVehicle(res.data);
                return getSimilarVehicles(id);
            })
            .then(res => setSimilar(res.data.vehicles || []))
            .catch(err => {
                setError('No se pudo cargar el vehículo.');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [id]);

    const handleWhatsApp = () => {
        trackEvent({ event_type: 'whatsapp_click', vehicle_id: vehicle?.id, vehicle_title: vehicle?.title }).catch(() => {});
    };

    const handleCall = () => {
        trackEvent({ event_type: 'call_click', vehicle_id: vehicle?.id, vehicle_title: vehicle?.title }).catch(() => {});
    };

    const handleCopyInfo = () => {
        if (!vehicle) return;
        const info = `${vehicle.brand} ${vehicle.model} (${vehicle.year}) - ${formatPrice(vehicle.price)} - ${formatKm(vehicle.km)} - ${vehicle.fuel || ''} - ${window.location.href}`;
        navigator.clipboard.writeText(info).then(() => {
            toast.success('Información copiada al portapapeles');
        }).catch(() => {
            toast.error('No se pudo copiar');
        });
        trackEvent({ event_type: 'copy_info', vehicle_id: vehicle?.id, vehicle_title: vehicle?.title }).catch(() => {});
    };

    if (loading) {
        return (
            <div className="py-6 sm:py-8">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <Skeleton className="h-6 w-48 mb-6" />
                    <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
                        <Skeleton className="aspect-[16/10] rounded-xl" />
                        <div className="space-y-4">
                            <Skeleton className="h-8 w-3/4" />
                            <Skeleton className="h-10 w-1/3" />
                            <Skeleton className="h-32" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !vehicle) {
        return (
            <div className="py-16">
                <Card className="max-w-md mx-auto p-8 text-center">
                    <Car className="h-12 w-12 mx-auto mb-3 text-[hsl(var(--muted-foreground))]/30" />
                    <h2 className="font-[var(--font-display)] font-semibold mb-2">Vehículo no encontrado</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{error || 'Este vehículo no está disponible.'}</p>
                    <Button asChild variant="outline"><Link to="/coches">Ver inventario</Link></Button>
                </Card>
            </div>
        );
    }

    const displayTitle = vehicle.custom_title || vehicle.title || `${vehicle.brand} ${vehicle.model}`;
    const displayPrice = vehicle.custom_price || vehicle.price;
    const pageUrl = window.location.href;
    const statusBadge = getStatusBadge(vehicle.status);

    const specs = [
        { icon: Calendar, label: 'Año', value: vehicle.year },
        { icon: Gauge, label: 'Kilómetros', value: vehicle.km ? formatKm(vehicle.km) : null },
        { icon: Fuel, label: 'Combustible', value: vehicle.fuel },
        { icon: Zap, label: 'Potencia', value: vehicle.horsepower ? `${vehicle.horsepower} cv` : null },
        { icon: Car, label: 'Cambio', value: vehicle.transmission },
        { icon: MapPin, label: 'Ubicación', value: vehicle.location },
        { icon: Shield, label: 'Garantía', value: vehicle.warranty },
        { icon: CreditCard, label: 'Financiación', value: vehicle.financing },
    ].filter(s => s.value);

    return (
        <div className="pb-28 md:pb-8" data-testid="vehicle-detail-page">
            <SEO
                title={`${displayTitle} - ${formatPrice(displayPrice)}`}
                description={`${displayTitle} (${vehicle.year || ''}) - ${formatPrice(displayPrice)} - ${vehicle.km ? formatKm(vehicle.km) : ''} - ${vehicle.fuel || ''}. Concesionario ASG Motor en Don Benito, Badajoz. Garantía incluida.`}
                path={`/coches/${vehicle.id}`}
                image={vehicle.images?.[0]}
                type="product"
                schema={vehicleSchema(vehicle)}
            />
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                {/* Breadcrumb */}
                <Breadcrumb className="mb-4 text-xs sm:text-sm overflow-hidden">
                    <BreadcrumbList className="flex-nowrap">
                        <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Inicio</Link></BreadcrumbLink></BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem><BreadcrumbLink asChild><Link to="/coches">Vehículos</Link></BreadcrumbLink></BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem><BreadcrumbPage className="truncate max-w-[180px] sm:max-w-none">{displayTitle}</BreadcrumbPage></BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>

                <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
                    {/* Left: Gallery */}
                    <div className="min-w-0">
                        <PhotoGallery images={vehicle.images || []} title={displayTitle} />
                    </div>

                    {/* Right: Info + CTAs */}
                    <div className="lg:sticky lg:top-20 lg:self-start">
                        <Card className="rounded-xl p-4 sm:p-5">
                            {/* Status badges */}
                            <div className="flex gap-2 mb-3">
                                {vehicle.status !== 'available' && (
                                    <Badge variant={statusBadge.variant} data-testid="vehicle-status-badge">{statusBadge.label}</Badge>
                                )}
                                {vehicle.eco_label && (
                                    <Badge variant="outline" className={getEcoLabelColor(vehicle.eco_label)} data-testid="vehicle-eco-label">{vehicle.eco_label}</Badge>
                                )}
                                {vehicle.featured && (
                                    <Badge className="bg-[var(--brand-blue)] text-white">Destacado</Badge>
                                )}
                            </div>

                            <h1 className="font-[var(--font-display)] tracking-tight text-xl sm:text-2xl font-bold text-[hsl(var(--foreground))] mb-2" data-testid="vehicle-title">
                                {displayTitle}
                            </h1>

                            <p className="font-[var(--font-display)] text-3xl font-bold text-[var(--brand-navy)] mb-4" data-testid="vehicle-price">
                                {formatPrice(displayPrice)}
                            </p>

                            {/* CTAs */}
                            <div className="space-y-2 mb-5">
                                <a href={buildWhatsAppUrl(settings, vehicle, pageUrl)} target="_blank" rel="noopener noreferrer" onClick={handleWhatsApp} className="block">
                                    <Button className="w-full h-12 bg-[#25D366] text-white hover:bg-[#20BD59] focus-visible:ring-[#25D366]/40 text-base gap-2" data-testid="vehicle-detail-whatsapp-button">
                                        <MessageCircle className="h-5 w-5" />
                                        Hablar por WhatsApp
                                    </Button>
                                </a>
                                <div className="flex gap-2">
                                    <a href={`tel:${settings?.phone || '+34600000000'}`} onClick={handleCall} className="flex-1">
                                        <Button variant="outline" className="w-full h-10 gap-2" data-testid="vehicle-detail-call-button">
                                            <Phone className="h-4 w-4" />
                                            Llamar
                                        </Button>
                                    </a>
                                    <Button variant="outline" className="h-10 gap-2" onClick={handleCopyInfo} data-testid="vehicle-detail-copy-info-button">
                                        <Copy className="h-4 w-4" />
                                        Copiar
                                    </Button>
                                </div>
                            </div>

                            <Separator className="mb-4" />

                            {/* Specs */}
                            <div className="space-y-2.5">
                                {specs.map((s, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                                            <s.icon className="h-4 w-4" />
                                            {s.label}
                                        </span>
                                        <span className="font-medium">{s.value}</span>
                                    </div>
                                ))}
                            </div>

                            {vehicle.original_url && (
                                <a href={vehicle.original_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[var(--brand-blue)] hover:underline mt-4">
                                    Ver anuncio original <ArrowUpRight className="h-3 w-3" />
                                </a>
                            )}
                        </Card>
                    </div>
                </div>

                {/* Description */}
                {(vehicle.description || vehicle.custom_description) && (
                    <div className="mt-8">
                        <h2 className="font-[var(--font-display)] font-semibold text-lg mb-3">Descripción</h2>
                        <Card className="p-5 rounded-xl">
                            <p className="text-sm leading-relaxed text-[hsl(var(--muted-foreground))] whitespace-pre-line">
                                {vehicle.custom_description || vehicle.description}
                            </p>
                        </Card>
                    </div>
                )}

                {/* Extras */}
                {vehicle.extras && vehicle.extras.length > 0 && (
                    <div className="mt-6">
                        <h2 className="font-[var(--font-display)] font-semibold text-lg mb-3">Equipamiento</h2>
                        <div className="flex flex-wrap gap-2">
                            {vehicle.extras.map((extra, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{extra}</Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Similar vehicles */}
                {similar.length > 0 && (
                    <div className="mt-10">
                        <h2 className="font-[var(--font-display)] font-semibold text-lg mb-4">Vehículos similares</h2>
                        <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                            {similar.map(v => <VehicleCard key={v.id} vehicle={v} />)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
